import { useEffect, useRef, useState } from "react";
import { X, Plus, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/*
 * Iter300/302 — Sponsor Ribbon manager.
 * Owner-only modal opened from the OPS header. Iter302 adds:
 *   · Logo file upload (JPG/PNG/WEBP/SVG ≤ 3 MB) with live preview
 *   · Unified `contact` field — URL / phone / short note
 *   · `logo_url` is no longer required; name + contact is enough
 */
const CONTACT_HELP = "Website, phone number, or a short note (optional)";
const ZONE_OPTIONS = [
  { key: "header", label: "Header" },
  { key: "footer", label: "Footer" },
];

// Iter313 — Grandfather legacy /uploads paths in the admin preview
// exactly the way the public viewer does. Absolute urls pass through.
function resolveLogoAdmin(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  if (s.startsWith("/api/")) return `${backend}${s}`;
  if (s.startsWith("/uploads/")) return `${backend}/api${s}`;
  if (s.startsWith("/")) return `${backend}${s}`;
  return s;
}

export default function SponsorRibbonModal({ festivalId, open, onClose }) {
  const [sponsors, setSponsors] = useState([]);
  const [ribbon, setRibbon] = useState({ enabled: true, position: "bottom", rotate_seconds: 60 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ name: "", logo_url: "", contact: "", zones: ["header"] });
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/festival/${festivalId}/sponsors`)
      .then((r) => {
        setSponsors(r.data.sponsors || []);
        setRibbon(r.data.ribbon || ribbon);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Load failed"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, festivalId]);

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/festival/${festivalId}/sponsors/logo-upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, logo_url: r.data.logo_url }));
      toast.success("Logo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addSponsor = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim()) {
      toast.error("Sponsor name is required");
      return;
    }
    if (!form.logo_url && !form.contact.trim()) {
      toast.error("Add a logo or a contact detail so there's something to display");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/festival/${festivalId}/sponsors`, {
        name: form.name.trim(),
        logo_url: form.logo_url || null,
        contact: form.contact.trim() || null,
        zones: form.zones && form.zones.length ? form.zones : ["header"],
      });
      setSponsors(r.data.sponsors);
      setForm({ name: "", logo_url: "", contact: "", zones: ["header"] });
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Sponsor added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleFormZone = (zone) => {
    setForm((f) => {
      const next = new Set(f.zones || []);
      if (next.has(zone)) next.delete(zone); else next.add(zone);
      return { ...f, zones: Array.from(next) };
    });
  };

  const toggleSponsorZone = async (sid, zone) => {
    const cur = sponsors.find((s) => s.id === sid);
    if (!cur) return;
    const set = new Set(cur.zones || ["header"]);
    if (set.has(zone)) set.delete(zone); else set.add(zone);
    const next = Array.from(set);
    if (next.length === 0) {
      toast.error("A sponsor must be visible in at least one zone.");
      return;
    }
    try {
      const r = await api.patch(`/festival/${festivalId}/sponsors/${sid}`, { zones: next });
      setSponsors((list) => list.map((s) => s.id === sid ? r.data.sponsor : s));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const removeSponsor = async (sid) => {
    try {
      const r = await api.delete(`/festival/${festivalId}/sponsors/${sid}`);
      setSponsors(r.data.sponsors);
      toast.success("Sponsor removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  const patchRibbon = async (patch) => {
    const next = { ...ribbon, ...patch };
    setRibbon(next);
    try {
      const r = await api.patch(`/festival/${festivalId}/sponsor-ribbon`, patch);
      setRibbon(r.data.ribbon);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  if (!open) return null;

  const previewHref = form.logo_url?.startsWith("/") ? form.logo_url : form.logo_url;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="sponsor-ribbon-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border border-[#262626] text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626] sticky top-0 bg-[#0A0A0A]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#CCFF00]">Iter302 · Activation</div>
            <div className="text-lg font-bold">Sponsor Ribbon</div>
          </div>
          <button
            onClick={onClose}
            data-testid="sponsor-modal-close"
            className="text-[#A3A3A3] hover:text-white"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Ribbon settings */}
        <div className="p-5 border-b border-[#262626] space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => patchRibbon({ enabled: !ribbon.enabled })}
              data-testid="ribbon-enabled-toggle"
              className={`px-3 py-1.5 text-[10px] uppercase tracking-wider border ${ribbon.enabled ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-[#525252] text-[#A3A3A3] hover:border-[#CCFF00]"}`}
            >
              Ribbon: {ribbon.enabled ? "ON" : "OFF"}
            </button>
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              Position:
              <select
                value={ribbon.position}
                onChange={(e) => patchRibbon({ position: e.target.value })}
                data-testid="ribbon-position"
                className="bg-transparent border border-[#262626] px-2 py-1 text-white focus:outline-none focus:border-[#CCFF00]"
              >
                <option value="top" className="bg-black">Top of wall</option>
                <option value="bottom" className="bg-black">Bottom of wall</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              Rotate every:
              <input
                type="number"
                min={3}
                max={60}
                value={ribbon.rotate_seconds}
                onChange={(e) => setRibbon((r) => ({ ...r, rotate_seconds: Number(e.target.value) || 0 }))}
                onBlur={(e) => {
                  const v = Math.max(3, Math.min(60, Number(e.target.value) || 6));
                  patchRibbon({ rotate_seconds: v });
                }}
                data-testid="ribbon-rotate-seconds"
                className="w-16 bg-transparent border border-[#262626] px-2 py-1 text-white focus:outline-none focus:border-[#CCFF00]"
              />
              <span>s</span>
            </label>
          </div>
          <p className="text-[10px] text-[#525252] leading-relaxed">
            The Public Viewer wall and player self-service pages show a rotating strip of sponsor cards.
            Each card holds for the number of seconds set above. If a logo is uploaded it appears alongside the name;
            otherwise just the name and contact detail render. Contact URLs are tap-through, phone numbers dial,
            everything else shows as plain text.
          </p>
        </div>

        {/* Add form */}
        <form onSubmit={addSponsor} className="p-5 border-b border-[#262626] space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-[#CCFF00]">Add sponsor</div>
          <input
            type="text"
            placeholder="Sponsor name (required)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="sponsor-form-name"
            className="w-full bg-transparent border border-[#262626] px-3 py-2 text-white text-sm focus:outline-none focus:border-[#CCFF00]"
            maxLength={80}
          />
          <div className="flex items-center gap-3">
            <div className="w-16 h-14 bg-white border border-[#262626] flex items-center justify-center overflow-hidden shrink-0">
              {form.logo_url ? (
                <img src={resolveLogoAdmin(previewHref)} alt="Preview" className="max-w-full max-h-full object-contain" />
              ) : (
                <ImageIcon className="w-5 h-5 text-[#525252]" />
              )}
            </div>
            <label className="flex-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                onChange={(e) => uploadLogo(e.target.files?.[0])}
                data-testid="sponsor-form-logo-file"
                className="hidden"
              />
              <span
                className={`inline-flex items-center gap-2 px-3 py-2 border border-[#262626] text-[11px] uppercase tracking-wider ${uploading ? "opacity-60" : "hover:border-[#CCFF00] hover:text-[#CCFF00] cursor-pointer"}`}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                data-testid="sponsor-form-logo-btn"
              >
                <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : form.logo_url ? "Replace logo" : "Upload logo (optional)"}
              </span>
              {form.logo_url && (
                <button
                  type="button"
                  onClick={() => { setForm((f) => ({ ...f, logo_url: "" })); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  data-testid="sponsor-form-logo-clear"
                  className="ml-2 text-[10px] uppercase tracking-wider text-[#A3A3A3] hover:text-[#FF4500]"
                >
                  Clear
                </button>
              )}
              <div className="text-[10px] text-[#525252] mt-1">JPG / PNG / WEBP / SVG · max 3 MB</div>
            </label>
          </div>
          <div>
            <input
              type="text"
              placeholder={CONTACT_HELP}
              value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              data-testid="sponsor-form-contact"
              className="w-full bg-transparent border border-[#262626] px-3 py-2 text-white text-sm focus:outline-none focus:border-[#CCFF00]"
              maxLength={200}
            />
            <div className="text-[10px] text-[#525252] mt-1">
              Examples: <code className="text-[#A3A3A3]">https://acme.co</code> · <code className="text-[#A3A3A3]">+44 7700 900123</code> · <code className="text-[#A3A3A3]">Sign up at the desk!</code>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Display zone(s)</div>
            <div className="flex gap-2" data-testid="sponsor-form-zones">
              {ZONE_OPTIONS.map((z) => {
                const active = (form.zones || []).includes(z.key);
                return (
                  <button
                    key={z.key}
                    type="button"
                    onClick={() => toggleFormZone(z.key)}
                    data-testid={`sponsor-form-zone-${z.key}`}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-wider border ${active ? "bg-[#CCFF00] text-black border-[#CCFF00] font-bold" : "border-[#262626] text-[#A3A3A3] hover:border-[#CCFF00]"}`}
                  >
                    {z.label}{active ? " ✓" : ""}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-[#525252] mt-1">Pick where this sponsor appears on the wall + mobile view. Both zones is allowed; empty defaults to header.</div>
          </div>
          <button
            type="submit"
            disabled={saving || uploading}
            data-testid="sponsor-form-add"
            className="px-3 py-1.5 text-[10px] uppercase tracking-wider bg-[#CCFF00] text-black font-bold hover:bg-[#B8E600] disabled:opacity-50 inline-flex items-center gap-1"
          >
            <Plus className="w-3 h-3" /> Add sponsor
          </button>
        </form>

        {/* Sponsor list */}
        <div className="p-5" data-testid="sponsor-list">
          <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-3">
            {sponsors.length === 0 ? "No sponsors yet" : `${sponsors.length} sponsor${sponsors.length === 1 ? "" : "s"} in ribbon`}
          </div>
          {loading && <div className="text-[#525252] text-sm">Loading…</div>}
          <div className="space-y-2">
            {sponsors.map((s) => {
              const contact = s.contact || s.link_url;
              return (
                <div
                  key={s.id}
                  data-testid={`sponsor-row-${s.id}`}
                  className="flex items-center gap-3 border border-[#262626] px-3 py-2"
                >
                  <div className="w-14 h-10 bg-white border border-[#262626] flex items-center justify-center overflow-hidden shrink-0">
                    {s.logo_url ? (
                      <img src={resolveLogoAdmin(s.logo_url)} alt={s.name} className="max-w-full max-h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ) : (
                      <ImageIcon className="w-4 h-4 text-[#525252]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{s.name}</div>
                    {contact && <div className="text-[10px] text-[#66C2FF] truncate">{contact}</div>}
                    {!s.logo_url && !contact && (
                      <div className="text-[10px] text-[#FFB020]">Name only — add a logo or contact</div>
                    )}
                    <div className="mt-1 flex gap-1" data-testid={`sponsor-row-zones-${s.id}`}>
                      {ZONE_OPTIONS.map((z) => {
                        const active = (s.zones || ["header"]).includes(z.key);
                        return (
                          <button
                            key={z.key}
                            type="button"
                            onClick={() => toggleSponsorZone(s.id, z.key)}
                            data-testid={`sponsor-row-zone-${z.key}-${s.id}`}
                            className={`px-2 py-0.5 text-[9px] uppercase tracking-widest border ${active ? "bg-[#CCFF00] text-black border-[#CCFF00] font-bold" : "border-[#262626] text-[#A3A3A3] hover:border-[#CCFF00]"}`}
                          >
                            {z.label}{active ? " ✓" : ""}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSponsor(s.id)}
                    data-testid={`sponsor-remove-${s.id}`}
                    className="text-[#A3A3A3] hover:text-[#FF4500] p-1"
                    title="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
